import { useState, useCallback } from 'react';
import { Table, Guest } from '../types';

interface UseDragDropOptions {
  tables: Table[];
  setTables: React.Dispatch<React.SetStateAction<Table[]>>;
  saveSeatingArrangement: (tables: Table[]) => void;
  editingDisabled: boolean;
}

export function useDragDrop({ tables, setTables, saveSeatingArrangement, editingDisabled }: UseDragDropOptions) {
  const [draggedGuest, setDraggedGuest] = useState<Guest | null>(null);

  const handleDragStart = useCallback((guest: Guest) => {
    setDraggedGuest(guest);
  }, []);

  const handleTableGuestDragStart = useCallback((guest: Guest, fromTableId: number) => {
    setDraggedGuest({ ...guest, sourceTableId: fromTableId });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((tableId: number) => {
    if (editingDisabled) {
      setDraggedGuest(null);
      return;
    }
    if (draggedGuest) {
      const updatedTables = tables.map(table => {
        const filteredGuests = table.guests.filter(g => g.id !== draggedGuest.id);

        if (table.id === tableId) {
          const currentCapacityUsed = filteredGuests.reduce(
            (sum, guest) => sum + (guest.guestCount || 1),
            0
          );
          const guestCount = draggedGuest.guestCount || 1;

          if (currentCapacityUsed + guestCount <= table.capacity) {
            const { sourceTableId, ...cleanGuest } = draggedGuest;
            return { ...table, guests: [...filteredGuests, cleanGuest] };
          } else {
            alert(
              `座位不足！該桌還有 ${table.capacity - currentCapacityUsed} 個座位，但需要 ${guestCount} 個座位。請先調整該桌的其他賓客安排。`
            );
            return { ...table, guests: filteredGuests };
          }
        }

        return { ...table, guests: filteredGuests };
      });
      setTables(updatedTables);
      saveSeatingArrangement(updatedTables);
    }
    setDraggedGuest(null);
  }, [draggedGuest, tables, setTables, saveSeatingArrangement, editingDisabled]);

  const handleDropToGuestArea = useCallback(() => {
    if (editingDisabled) {
      setDraggedGuest(null);
      return;
    }
    if (draggedGuest && (draggedGuest as Guest).sourceTableId) {
      const updatedTables = tables.map(table => ({
        ...table,
        guests: table.guests.filter(g => g.id !== draggedGuest.id),
      }));
      setTables(updatedTables);
      saveSeatingArrangement(updatedTables);
    }
    setDraggedGuest(null);
  }, [draggedGuest, tables, setTables, saveSeatingArrangement, editingDisabled]);

  return {
    draggedGuest,
    handleDragStart,
    handleTableGuestDragStart,
    handleDragOver,
    handleDrop,
    handleDropToGuestArea,
  };
}
